import _Map, { ViewState, Marker, MapRef } from "react-map-gl";
import {
  Coordinate,
  NavigationVoiceAndCoordinate,
  RouteChangeAlert,
  SpotType,
} from "~/generated/graphql.types";
import { SpotMarker } from "./marker/SpotMarker";
import { Route } from "./Route";
import GeocoderControl from "./GeocoderControl";
import { useContextMenu } from "react-contexify";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import "react-contexify/ReactContexify.css";
import { MapContextMenu } from "./MapContextMenu";
import { NavigationVoiceMarker } from "./marker/NavigationVoiceMarker";
import {
  useUpdateSpotMutation,
  useUpdateThumbnailMutation,
} from "./index.generated";
import {
  EditingHyperlapseContext,
  EditModeContext,
  validationLatLon,
} from "../..";
import { SpotForRoutingFragment } from "~/routes/_shared/fragments.generated";
import { HyperlapseSpotMarker } from "./marker/HyperlapseSpotMarker";
import { Box, Flex, useToast } from "@chakra-ui/react";
import { HyperlapseCurrentLocationMarker } from "./marker/HyperlapseCurrentLocationMarker";
import { BsPinAngleFill } from "react-icons/bs";
import {
  TrialDrivePassingPointFragment,
  TrialDriveSpotFragment,
} from "../../index.generated";
import { TrialDriveSpotMarker } from "./marker/TrialDriveSpotMarker";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

export const Map = ({
  courseId,
  isActiveChangeCourseAlertFlag,
  routeChangeAlert,
  isMapDarkMode,
  style,
  viewState,
  start,
  dest,
  waypoints,
  showTrialDrive,
  routeAllSpots,
  routeWithoutOpt,
  navigationPoints,
  trialDriveSpots,
  trialDrivePassingPointSegments,
  onChangeViewState,
  onClickLocation,
  focusOnSpot,
}: {
  courseId: string;
  isActiveChangeCourseAlertFlag: boolean | undefined;
  routeChangeAlert: RouteChangeAlert | null | undefined;
  isMapDarkMode: boolean;
  style?: Object;
  viewState: Partial<ViewState>;
  start?: SpotForRoutingFragment;
  dest?: SpotForRoutingFragment;
  waypoints: Array<SpotForRoutingFragment>;
  showTrialDrive: boolean;
  routeAllSpots: Array<Coordinate> | undefined;
  routeWithoutOpt: Array<Coordinate> | undefined;
  trialDriveSpots: Array<TrialDriveSpotFragment> | undefined;
  trialDrivePassingPointSegments:
    | Array<Array<TrialDrivePassingPointFragment>>
    | undefined;
  navigationPoints: Array<NavigationVoiceAndCoordinate> | undefined;
  onChangeViewState: (viewState: ViewState) => void;
  onClickLocation: (coordinate: Coordinate) => void;
  focusOnSpot: (coordinate: Coordinate) => void;
}) => {
  const toast = useToast();

  const [latestDragOperation, setLatestDragOperation] = useState<
    | {
        spotId: string;
        coordinate: Coordinate;
      }
    | undefined
  >();

  const [clickedLocation, setClickLocation] = useState<Coordinate>();
  const { editMode, spotIdForEdit, setSpotIdForEdit, isOpenHyperlapsePlayer } =
    useContext(EditModeContext);
  const { hyperlapseFrame } = useContext(EditingHyperlapseContext);
  const [updateSpotRes, updateSpot] = useUpdateSpotMutation();
  const [updateThumbnailRes, updateThumbnail] = useUpdateThumbnailMutation();
  // 作成中のhyperlapseのspots.
  const { editingHyperlapseSpots, setEditingHyperlapseSpots } = useContext(
    EditingHyperlapseContext
  );

  const onDragEndHyperlapseSpots = useCallback(
    ({
      spotOrder,
      dragEndCoord,
    }: {
      spotOrder: number;
      dragEndCoord: Coordinate;
    }) => {
      const newEditingyperlapseSpots = [...editingHyperlapseSpots];
      newEditingyperlapseSpots[spotOrder].lat = dragEndCoord.lat;
      newEditingyperlapseSpots[spotOrder].lon = dragEndCoord.lon;
      setEditingHyperlapseSpots(newEditingyperlapseSpots);
    },
    [editingHyperlapseSpots]
  );

  const { show: showContextMenu } = useContextMenu({
    id: "map_context_menu",
  });
  const [waypointsForRouting, setWaypointsForRouting] = useState<
    SpotForRoutingFragment[]
  >(
    waypoints.filter((waypoint) =>
      [
        SpotType.Waypoint,
        SpotType.OptionalWaypoint,
        SpotType.RoutingGuide,
        SpotType.SubaruMoment,
      ].includes(waypoint.spotType)
    )
  );
  useEffect(() => {
    setWaypointsForRouting(
      waypoints.filter((waypoint) =>
        [
          SpotType.Waypoint,
          SpotType.OptionalWaypoint,
          SpotType.RoutingGuide,
          SpotType.SubaruMoment,
        ].includes(waypoint.spotType)
      )
    );
  }, [waypoints]);
  // 上のコンポーネントにcoodinateを渡す。
  useEffect(() => {
    if (!clickedLocation) return;
    onClickLocation(clickedLocation);
  }, [clickedLocation]);

  useEffect(() => {
    const id = setInterval(() => {
      updateThumbnail({
        input: {
          courseId,
        },
      });
    }, 60 * 5 * 1000);
  }, []);

  const isSpotDraggable = useCallback(
    (spotType: SpotType) => {
      if (editMode === "routing") return true;
      if (editMode === "contents") {
        return [SpotType.Narration, SpotType.SubaruMoment].includes(spotType);
      }
    },
    [editMode]
  );

  const waypointsToDisplay =
    editMode === "routing"
      ? waypoints.filter((spot) =>
          [
            SpotType.Waypoint,
            SpotType.OptionalWaypoint,
            SpotType.RoutingGuide,
            SpotType.SubaruMoment,
          ].includes(spot?.spotType as SpotType)
        )
      : waypoints;

  // Spotの数などが変わるたびに、SelectedSpotsIndexをリセットする。
  useEffect(() => {
    setSpotIdForEdit(undefined);
  }, [[start, dest, ...waypoints].length]);

  //  mapのスタイルを変える。
  // https://visgl.github.io/react-map-gl/docs/api-reference/map
  const mapRef = useRef<MapRef>(null);
  useEffect(() => {
    if (!mapRef.current) return;
    if (isMapDarkMode) {
      mapRef.current.getMap().setStyle("mapbox://styles/mapbox/dark-v11");
    } else {
      mapRef.current
        .getMap()
        .setStyle("mapbox://styles/theoroy/cl7cuuaai000214r0fs02ryug");
    }
  }, [isMapDarkMode]);

  return (
    <>
      <MapContextMenu
        contextMenuId="map_context_menu"
        courseId={courseId}
        clickedLocation={clickedLocation!}
        spotForRouting={
          [start, ...waypointsToDisplay, dest].filter(
            (v) => v
          ) as SpotForRoutingFragment[]
        }
        nextSpotCategory={
          !start
            ? SpotType.Start
            : !dest
            ? SpotType.Destination
            : SpotType.Waypoint
        }
        resetLatestDragOperation={() => setLatestDragOperation(undefined)}
        latestDragOperation={latestDragOperation}
      />
      <div onContextMenu={(e) => showContextMenu({ event: e })}>
        <_Map
          {...viewState}
          style={style}
          ref={mapRef}
          mapStyle="mapbox://styles/theoroy/cl7cuuaai000214r0fs02ryug"
          mapboxAccessToken={MAPBOX_TOKEN}
          onContextMenu={(e) => {
            setClickLocation({
              lat: e.lngLat.lat,
              lon: e.lngLat.lng,
            });
          }}
          onClick={(e) => {
            setClickLocation({
              lat: e.lngLat.lat,
              lon: e.lngLat.lng,
            });
          }}
          onMove={(evt) => onChangeViewState(evt.viewState)}
        >
          {start && (
            <SpotMarker
              spot={start}
              label="S"
              draggable={isSpotDraggable(start.spotType)}
              onDragEnd={(coord) => {
                const isValidLocation = validationLatLon({
                  lat: coord.lat,
                  lon: coord.lon,
                });

                setLatestDragOperation({
                  spotId: start.id,
                  coordinate: start.coordinate,
                });
                if (!isValidLocation.isValid) {
                  toast({
                    status: "error",
                    title: "入力値が不正です。",
                    description: isValidLocation.message,
                  });
                  return;
                }

                updateSpot(
                  {
                    input: {
                      id: start.id,
                      coordinate: coord,
                    },
                  },
                  {
                    additionalTypenames: ["Course", "RoutingInfomationPayload"],
                  }
                );
              }}
              onClick={() => {
                setSpotIdForEdit(start.id);
                focusOnSpot(start.coordinate);
              }}
              isSelected={spotIdForEdit === start.id}
            />
          )}
          {/* ToDo 今は無理矢理spots同じSpotTypeの要素を取ってきて長さを取得することで、orderを取ってきているが、効率がわるい。Spotの要素にしたい。*/}
          {waypointsToDisplay.map((spot, i) => (
            <SpotMarker
              key={i}
              spot={spot}
              draggable={isSpotDraggable(spot.spotType)}
              label={waypointsToDisplay
                .slice(0, i + 1)
                .filter((p) => p?.spotType === spot.spotType)
                .length.toString()}
              onClick={() => {
                setSpotIdForEdit(spot.id);
                focusOnSpot(spot.coordinate);
              }}
              isSelected={spotIdForEdit === spot.id}
              onDragEnd={(coord) => {
                const isValidLocation = validationLatLon({
                  lat: coord.lat,
                  lon: coord.lon,
                });

                if (!isValidLocation.isValid) {
                  toast({
                    status: "error",
                    title: "入力値が不正です。",
                    description: isValidLocation.message,
                  });
                  return;
                }

                setLatestDragOperation({
                  spotId: waypointsToDisplay[i].id,
                  coordinate: waypointsToDisplay[i].coordinate,
                });
                updateSpot(
                  {
                    input: {
                      id: waypointsToDisplay[i].id,
                      coordinate: coord,
                    },
                  },
                  {
                    additionalTypenames: ["Course", "RoutingInfomationPayload"],
                  }
                );
              }}
            />
          ))}
          {/* ToDo 今は無理矢理spots同じSpotTypeの要素を取ってきて長さを取得することで、orderを取ってきているが、効率がわるい。Spotの要素にしたい。*/}
          {dest && (
            <SpotMarker
              spot={dest}
              draggable={isSpotDraggable(dest.spotType)}
              onDragEnd={(coord) => {
                const isValidLocation = validationLatLon({
                  lat: coord.lat,
                  lon: coord.lon,
                });

                if (!isValidLocation.isValid) {
                  toast({
                    status: "error",
                    title: "入力値が不正です。",
                    description: isValidLocation.message,
                  });
                  return;
                }
                setLatestDragOperation({
                  spotId: dest.id,
                  coordinate: dest.coordinate,
                });
                updateSpot(
                  {
                    input: {
                      id: dest.id,
                      coordinate: coord,
                    },
                  },
                  {
                    additionalTypenames: ["Course", "RoutingInfomationPayload"],
                  }
                );
              }}
              label="G"
              onClick={() => {
                setSpotIdForEdit(dest.id);
                focusOnSpot(dest.coordinate);
              }}
              isSelected={spotIdForEdit === dest.id}
            />
          )}

          {clickedLocation ? (
            <Marker
              latitude={Number(clickedLocation.lat)}
              longitude={Number(clickedLocation.lon)}
              style={{ zIndex: 100 }}
            >
              <Flex position="relative" bottom={4} zIndex={200}>
                <BsPinAngleFill color={"#000000"} size={20} />
              </Flex>
            </Marker>
          ) : null}
          {navigationPoints && editMode === "contents"
            ? navigationPoints.map((spot, i) => (
                <NavigationVoiceMarker key={i} navigationVoiceSpot={spot} />
              ))
            : null}
          {editMode === "contents" && editingHyperlapseSpots.length > 0
            ? editingHyperlapseSpots.map((coordinate, i) => (
                <HyperlapseSpotMarker
                  key={i}
                  coordinate={coordinate}
                  label={
                    i === 0
                      ? "S"
                      : i === editingHyperlapseSpots.length - 1
                      ? "G"
                      : i.toString()
                  }
                  onDragEnd={(coord) => {
                    onDragEndHyperlapseSpots({
                      spotOrder: i,
                      dragEndCoord: coord,
                    });
                  }}
                />
              ))
            : null}
          {isActiveChangeCourseAlertFlag && routeChangeAlert && (
            <PrevRoute routeChangeAlert={routeChangeAlert} />
          )}
          {routeAllSpots ? (
            <Route
              id={`route-with-optional-waypoints`}
              color="#81C784"
              dotted
              route={routeAllSpots}
            />
          ) : null}
          {routeWithoutOpt ? (
            <Route
              id={`route-without-optional-waypoints`}
              color={"#FF4500"}
              route={routeWithoutOpt}
            />
          ) : null}
          {trialDrivePassingPointSegments &&
            showTrialDrive &&
            trialDrivePassingPointSegments.map((segment, idx) => (
              <Route
                key={idx}
                id={`route-trial-drive-${idx}`}
                color={"#1E6CF2"}
                route={segment.map((passingPoint, idx) => {
                  return {
                    lat: passingPoint.latitude,
                    lon: passingPoint.longitude,
                  };
                })}
              />
            ))}
          {trialDriveSpots &&
            showTrialDrive &&
            trialDriveSpots.map((spot, idx) => (
              <TrialDriveSpotMarker key={spot.trialDriveSpotId} spot={spot} />
            ))}
          <GeocoderControl
            reverseGeocode
            countries="jp"
            mapboxAccessToken={MAPBOX_TOKEN}
            position="top-left"
          />
          {hyperlapseFrame && isOpenHyperlapsePlayer ? (
            <HyperlapseCurrentLocationMarker
              coordinate={hyperlapseFrame.coordinate}
              heading={hyperlapseFrame.heading}
            />
          ) : null}
        </_Map>
      </div>
    </>
  );
};

const PrevRoute = ({
  routeChangeAlert,
}: {
  routeChangeAlert: RouteChangeAlert;
}) => {
  return (
    <Route
      id={`prev-route`}
      color="#FF8EFB"
      route={routeChangeAlert.route.map((passingPoint, idx) => {
        return {
          lat: passingPoint.lat,
          lon: passingPoint.lon,
        };
      })}
    />
  );
};
